import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Phone, Mail, User } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientManagement() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  
  const [formData, setFormData] = useState({
    client_name: '',
    billing_person_name: '',
    billing_person_email: '',
    billing_person_phone: '',
    pm_name: '',
    pm_email: '',
    pm_phone: '',
    coordinator_name: '',
    coordinator_email: '',
    coordinator_phone: '',
    notes: ''
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (error) {
      toast.error('Failed to load clients');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    try {
      const url = editingClient 
        ? `${API_URL}/api/clients/${editingClient.id}`
        : `${API_URL}/api/clients`;
      
      const method = editingClient ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingClient ? 'Client updated!' : 'Client created!');
        setShowDialog(false);
        resetForm();
        fetchClients();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Operation failed');
      }
    } catch (error) {
      toast.error('Connection error');
      console.error(error);
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      client_name: client.client_name || '',
      billing_person_name: client.billing_person_name || '',
      billing_person_email: client.billing_person_email || '',
      billing_person_phone: client.billing_person_phone || '',
      pm_name: client.pm_name || '',
      pm_email: client.pm_email || '',
      pm_phone: client.pm_phone || '',
      coordinator_name: client.coordinator_name || '',
      coordinator_email: client.coordinator_email || '',
      coordinator_phone: client.coordinator_phone || '',
      notes: client.notes || ''
    });
    setShowDialog(true);
  };

  const handleDelete = async (clientId) => {
    if (!confirm('Delete this client? This cannot be undone if projects are linked.')) return;
    
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/clients/${clientId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Client deleted');
        fetchClients();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete');
      }
    } catch (error) {
      toast.error('Connection error');
      console.error(error);
    }
  };

  const resetForm = () => {
    setEditingClient(null);
    setFormData({
      client_name: '',
      billing_person_name: '',
      billing_person_email: '',
      billing_person_phone: '',
      pm_name: '',
      pm_email: '',
      pm_phone: '',
      coordinator_name: '',
      coordinator_email: '',
      coordinator_phone: '',
      notes: ''
    });
  };

  const openCreateDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  if (loading) return <div>Loading clients...</div>;

  return (
    <div className="client-management">
      <div className="header-actions" style={{ marginBottom: '1.5rem' }}>
        <Button onClick={openCreateDialog}>
          <Plus className="w-4 h-4 mr-2" />
          Add Client
        </Button>
      </div>

      {clients.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-gray-500 mb-4">No clients yet</p>
            <Button onClick={openCreateDialog}>
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Client
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="clients-grid">
          {clients.map((client) => (
            <Card key={client.id} className="client-card">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <Badge variant="secondary" className="mb-2">
                      {client.client_code}
                    </Badge>
                    <CardTitle>{client.client_name}</CardTitle>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(client)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(client.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Billing Contact */}
                {client.billing_person_name && (
                  <div className="contact-section">
                    <p className="text-sm font-semibold text-gray-700 mb-1">
                      <User className="w-3 h-3 inline mr-1" />
                      Billing Contact
                    </p>
                    <p className="text-sm">{client.billing_person_name}</p>
                    {client.billing_person_email && (
                      <p className="text-xs text-gray-600">
                        <Mail className="w-3 h-3 inline mr-1" />
                        {client.billing_person_email}
                      </p>
                    )}
                    {client.billing_person_phone && (
                      <p className="text-xs text-gray-600">
                        <Phone className="w-3 h-3 inline mr-1" />
                        {client.billing_person_phone}
                      </p>
                    )}
                  </div>
                )}

                {/* Project Manager */}
                {client.pm_name && (
                  <div className="contact-section">
                    <p className="text-sm font-semibold text-gray-700 mb-1">
                      <User className="w-3 h-3 inline mr-1" />
                      Project Manager
                    </p>
                    <p className="text-sm">{client.pm_name}</p>
                    {client.pm_email && (
                      <p className="text-xs text-gray-600">
                        <Mail className="w-3 h-3 inline mr-1" />
                        {client.pm_email}
                      </p>
                    )}
                    {client.pm_phone && (
                      <p className="text-xs text-gray-600">
                        <Phone className="w-3 h-3 inline mr-1" />
                        {client.pm_phone}
                      </p>
                    )}
                  </div>
                )}

                {/* Coordinator */}
                {client.coordinator_name && (
                  <div className="contact-section">
                    <p className="text-sm font-semibold text-gray-700 mb-1">
                      <User className="w-3 h-3 inline mr-1" />
                      Coordinator
                    </p>
                    <p className="text-sm">{client.coordinator_name}</p>
                    {client.coordinator_email && (
                      <p className="text-xs text-gray-600">
                        <Mail className="w-3 h-3 inline mr-1" />
                        {client.coordinator_email}
                      </p>
                    )}
                    {client.coordinator_phone && (
                      <p className="text-xs text-gray-600">
                        <Phone className="w-3 h-3 inline mr-1" />
                        {client.coordinator_phone}
                      </p>
                    )}
                  </div>
                )}

                {client.notes && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-xs text-gray-600">{client.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingClient ? 'Edit Client' : 'Add New Client'}
            </DialogTitle>
            <DialogDescription>
              {editingClient ? 'Update client information' : 'Create a new client with contact details'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Client Name */}
            <div>
              <Label htmlFor="client_name">Client Name *</Label>
              <Input
                id="client_name"
                required
                value={formData.client_name}
                onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                placeholder="ABC Construction Co."
              />
            </div>

            {/* Billing Contact */}
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3">Billing/Accounting Contact</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="billing_person_name">Name</Label>
                  <Input
                    id="billing_person_name"
                    value={formData.billing_person_name}
                    onChange={(e) => setFormData({...formData, billing_person_name: e.target.value})}
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <Label htmlFor="billing_person_email">Email</Label>
                  <Input
                    id="billing_person_email"
                    type="email"
                    value={formData.billing_person_email}
                    onChange={(e) => setFormData({...formData, billing_person_email: e.target.value})}
                    placeholder="john@company.com"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="billing_person_phone">Phone</Label>
                  <Input
                    id="billing_person_phone"
                    value={formData.billing_person_phone}
                    onChange={(e) => setFormData({...formData, billing_person_phone: e.target.value})}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
            </div>

            {/* Project Manager */}
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3">Project Manager</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="pm_name">Name</Label>
                  <Input
                    id="pm_name"
                    value={formData.pm_name}
                    onChange={(e) => setFormData({...formData, pm_name: e.target.value})}
                    placeholder="Jane Doe"
                  />
                </div>
                <div>
                  <Label htmlFor="pm_email">Email</Label>
                  <Input
                    id="pm_email"
                    type="email"
                    value={formData.pm_email}
                    onChange={(e) => setFormData({...formData, pm_email: e.target.value})}
                    placeholder="jane@company.com"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="pm_phone">Phone</Label>
                  <Input
                    id="pm_phone"
                    value={formData.pm_phone}
                    onChange={(e) => setFormData({...formData, pm_phone: e.target.value})}
                    placeholder="(555) 987-6543"
                  />
                </div>
              </div>
            </div>

            {/* Coordinator */}
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3">Project Coordinator</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="coordinator_name">Name</Label>
                  <Input
                    id="coordinator_name"
                    value={formData.coordinator_name}
                    onChange={(e) => setFormData({...formData, coordinator_name: e.target.value})}
                    placeholder="Mike Johnson"
                  />
                </div>
                <div>
                  <Label htmlFor="coordinator_email">Email</Label>
                  <Input
                    id="coordinator_email"
                    type="email"
                    value={formData.coordinator_email}
                    onChange={(e) => setFormData({...formData, coordinator_email: e.target.value})}
                    placeholder="mike@company.com"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="coordinator_phone">Phone</Label>
                  <Input
                    id="coordinator_phone"
                    value={formData.coordinator_phone}
                    onChange={(e) => setFormData({...formData, coordinator_phone: e.target.value})}
                    placeholder="(555) 246-8135"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="border-t pt-4">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Additional information about this client..."
                rows={3}
              />
            </div>

            <div className="flex gap-2">
              <Button type="submit" className="flex-1">
                {editingClient ? 'Update Client' : 'Create Client'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <style jsx>{`
        .clients-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 1.5rem;
        }

        .client-card {
          transition: box-shadow 0.2s;
        }

        .client-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .contact-section {
          margin-bottom: 1rem;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .contact-section:last-of-type {
          border-bottom: none;
          margin-bottom: 0;
        }

        @media (max-width: 768px) {
          .clients-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}
